import { useState, useMemo, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { DismissibleTooltip } from '../../common/DismissibleTooltip'
import { X, Minimize2, Maximize2 } from 'lucide-react'
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css'
import { COLORS } from '../../../styles/theme'
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  type MessageModel,
} from '@chatscope/chat-ui-kit-react'
import type { FeedbackContext } from '../../../context/ScopedChatContext'
import {
  ChatWindowContainer,
  ChatHeader,
  HeaderContent,
  HeaderTitleWrapper,
  ChatContainerWrapper,
  HeaderTitle,
  HeaderSubtitle,
  FeedbackBadge,
  HeaderActions,
  HeaderButton,
  ChatContent,
} from './styles/ChatScopeChatWindowStyles'

interface ChatMessage {
  id: string
  content: string
  sender: 'user' | 'ai'
  timestamp: Date
}

interface ChatScopeChatWindowProps {
  isVisible: boolean
  onClose: () => void
  onSendMessage: (message: string) => Promise<void>
  messages: ChatMessage[]
  isLoading: boolean
  stakeholderName?: string | null
  stakeholderRole?: string | null
  /** Optional feedback context for scoped conversations */
  feedbackContext?: FeedbackContext | null
}

/**
 * Get a short label for the feedback type badge
 */
function getFeedbackTypeBadge(feedbackType: string): string {
  switch (feedbackType) {
    case 'concerns':
      return 'Concern'
    case 'suggestions':
      return 'Suggestion'
    case 'question':
      return 'Question'
    default:
      return 'Feedback'
  }
}

export function ChatScopeChatWindow({
  isVisible,
  onClose,
  onSendMessage,
  messages,
  isLoading,
  stakeholderName,
  stakeholderRole,
  feedbackContext,
}: ChatScopeChatWindowProps) {
  const [isMinimized, setIsMinimized] = useState(false)
  const chatContainerRef = useRef<HTMLDivElement>(null)

  // Get display name and role
  const displayName = stakeholderName ?? 'AI Assistant'
  const displayRole = stakeholderRole

  // Replace ChatScope's send button SVG with Lucide Send icon
  useEffect(() => {
    if (!isVisible || !chatContainerRef.current) return undefined

    const replaceSendButtonIcon = () => {
      const sendButton = chatContainerRef.current?.querySelector(
        '.cs-button--send, .cs-message-input__send-button'
      )
      if (sendButton) {
        const buttonEl = sendButton as HTMLElement
        buttonEl.style.backgroundColor = COLORS.primary
        buttonEl.style.borderColor = COLORS.primary

        // Check if we've already replaced it
        const existingLucideIcon = sendButton.querySelector('.lucide-send')
        if (existingLucideIcon) {
          // Ensure existing icon is white
          const svg = existingLucideIcon as SVGElement
          svg.setAttribute('stroke', '#fff')
          svg.style.stroke = '#fff'
          const paths = svg.querySelectorAll('path')
          paths.forEach((path) => {
            path.setAttribute('stroke', '#fff')
            path.style.stroke = '#fff'
          })
          return
        }

        // Remove existing SVG
        const existingSvg = sendButton.querySelector('svg:not(.lucide-send)')
        if (existingSvg) {
          existingSvg.remove()
        }

        // Insert Lucide Send SVG via static markup
        sendButton.insertAdjacentHTML(
          'beforeend',
          '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 20" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-send" aria-hidden="true" style="stroke: #fff; fill: none;"><path d="m22 2-7 20-4-9-9-4Z" stroke="#fff" style="stroke: #fff;"/><path d="M22 2 11 13" stroke="#fff" style="stroke: #fff;"/></svg>'
        )
      }
    }

    // Update immediately and on mutations
    replaceSendButtonIcon()
    const observer = new MutationObserver(replaceSendButtonIcon)
    observer.observe(chatContainerRef.current, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [isVisible, messages, isLoading])

  // Convert messages to ChatScope format
  const chatScopeMessages: (MessageModel & { originalId?: string })[] = useMemo(() => {
    return messages.map((msg) => ({
      message: msg.content,
      sentTime: msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      sender: msg.sender === 'user' ? 'You' : displayName,
      direction: msg.sender === 'user' ? 'outgoing' : 'incoming',
      position: 'single',
      originalId: msg.id,
    }))
  }, [messages, displayName])

  // Build initial context message if feedbackContext exists
  const initialContextMessage = useMemo((): MessageModel | null => {
    if (!feedbackContext) return null

    const feedbackTypeText =
      feedbackContext.feedbackType === 'concerns'
        ? 'concern'
        : feedbackContext.feedbackType === 'suggestions'
          ? 'suggestion'
          : 'question'

    const message = `I'd like to discuss this ${feedbackTypeText} about "${feedbackContext.ideaLabel}":\n\n"${feedbackContext.feedbackContent}"`

    return {
      message,
      sentTime: '',
      sender: displayName,
      direction: 'incoming',
      position: 'single',
    }
  }, [feedbackContext, displayName])

  // Add initial context message and loading message
  const messagesWithLoading = useMemo(() => {
    const allMessages = [...chatScopeMessages]

    // Prepend initial context message if it exists (always show at start of conversation)
    if (initialContextMessage) {
      allMessages.unshift(initialContextMessage)
    }

    // Add loading message if loading
    if (isLoading) {
      const loadingMessage: MessageModel = {
        message: 'let me think about that...',
        sentTime: 'just now',
        sender: displayName,
        direction: 'incoming',
        position: 'single',
      }
      allMessages.push(loadingMessage)
    }

    return allMessages
  }, [chatScopeMessages, isLoading, displayName, initialContextMessage])

  const handleSend = async (_innerHtml: string, textContent: string) => {
    if (!textContent.trim() || isLoading) return

    try {
      await onSendMessage(textContent.trim())
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'mindmap.chat', action: 'send_message_wrapper' },
      })
      // Error handling is done in onSendMessage
    }
  }

  const handleToggleMinimize = () => {
    setIsMinimized(!isMinimized)
  }

  const handleHeaderClick = () => {
    if (isMinimized) {
      setIsMinimized(false)
    }
  }

  if (!isVisible) return null

  return (
    <>
      <style>{`
                /* ChatScope container backgrounds and borders */
                .cs-main-container,
                .cs-chat-container,
                .cs-message-list {
                    background: var(--color-bg-elevated) !important;
                }

                .cs-main-container {
                    border: none !important;
                }

                .cs-chat-container .cs-message-input {
                    border-top-color: var(--color-border) !important;
                }

                /* Message bubble colors - match app theme */
                .cs-message--incoming .cs-message__content {
                    background: var(--color-bg-secondary) !important;
                    color: var(--color-text-primary) !important;
                }
                .cs-message--outgoing .cs-message__content {
                    background: var(--color-primary) !important;
                    color: #fff !important;
                }

                /* Dark theme specific styles */
                body[data-theme="dark"] .cs-message--incoming .cs-message__content {
                    background: #2a2a2a !important;
                    color: rgba(255, 255, 255, 0.85) !important;
                }

                /* Send button styling - only colors */
                .cs-button--send,
                .cs-message-input__send-button {
                    background: var(--color-primary) !important;
                    border-color: var(--color-primary) !important;
                    margin-right: 4px !important;
                }

                .cs-button--send:hover,
                .cs-message-input__send-button:hover {
                    background: var(--color-primary-hover) !important;
                    border-color: var(--color-primary-hover) !important;
                }
            `}</style>
      <ChatWindowContainer $isVisible={isVisible} $isMinimized={isMinimized} id="chatscope-window">
        <ChatHeader onClick={handleHeaderClick}>
          <HeaderContent>
            <HeaderTitleWrapper>
              <HeaderTitle>{displayName}</HeaderTitle>
              {!isMinimized && (
                <HeaderSubtitle>
                  {displayRole && <span>{displayRole}</span>}
                  {feedbackContext && (
                    <DismissibleTooltip
                      title={`"${feedbackContext.feedbackContent}" - Re: ${feedbackContext.ideaLabel}`}
                      placement="bottom"
                      mouseEnterDelay={0.2}
                    >
                      <FeedbackBadge>
                        {getFeedbackTypeBadge(feedbackContext.feedbackType)}
                      </FeedbackBadge>
                    </DismissibleTooltip>
                  )}
                </HeaderSubtitle>
              )}
            </HeaderTitleWrapper>
          </HeaderContent>
          <HeaderActions>
            <HeaderButton
              type="text"
              size="small"
              icon={isMinimized ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
              onClick={(e) => {
                e.stopPropagation()
                handleToggleMinimize()
              }}
              title={isMinimized ? 'Maximize' : 'Minimize'}
            />
            <HeaderButton
              type="text"
              size="small"
              icon={<X size={14} />}
              onClick={(e) => {
                e.stopPropagation()
                onClose()
              }}
              title="Close"
            />
          </HeaderActions>
        </ChatHeader>

        <ChatContent $isMinimized={isMinimized}>
          <ChatContainerWrapper ref={chatContainerRef}>
            <MainContainer>
              <ChatContainer>
                <MessageList>
                  {messagesWithLoading.map((msg, index) => (
                    <Message
                      key={
                        (msg as MessageModel & { originalId?: string }).originalId ??
                        `msg-${index}-${msg.sentTime}`
                      }
                      model={msg}
                    />
                  ))}
                </MessageList>
                <MessageInput
                  placeholder="Type your message..."
                  onSend={handleSend}
                  disabled={isLoading}
                  attachButton={false}
                />
              </ChatContainer>
            </MainContainer>
          </ChatContainerWrapper>
        </ChatContent>
      </ChatWindowContainer>
    </>
  )
}
